import {
  cn,
  Switch,
  SwitchProps,
  useSwitch,
  VisuallyHidden,
} from "@heroui/react";
import { MantineFormValues } from "./shared.types";
import React from "react";
import { LimeCollapse } from "./LimeCollapse";

type BaseSwitchProps = MantineFormValues;

type TextSwitchProps = BaseSwitchProps &
  SwitchProps & {
    switchType?: "text";
    description?: string;
    isCompact?: boolean;
  };

type IconSwitchProps = BaseSwitchProps &
  Omit<SwitchProps, "label" | "description" | "size"> & {
    switchType: "icon";
    enabledIcon: React.ReactNode;
    disabledIcon?: React.ReactNode;
    size?: number;
  };

type LimeSwitchProps = TextSwitchProps | IconSwitchProps;

export const LimeSwitch = React.forwardRef(
  (
    { error, children, ...props }: LimeSwitchProps,
    ref: React.Ref<HTMLInputElement>,
  ) => {
    if (props.switchType === "icon") {
      return <IconSwitch props={props}>{children}</IconSwitch>;
    }

    const {
      className,
      classNames,
      description,
      isCompact,
      title,
      ...switchProps
    } = props;

    return (
      <>
        <Switch
          className={cn(className, "touch-pan-y md:touch-none")}
          ref={ref}
          {...switchProps}
          classNames={{
            ...classNames,
            base: cn(
              "inline-flex flex-row-reverse w-full max-w-none bg-content1 hover:bg-content2 items-center",
              "justify-between cursor-pointer rounded-lg gap-2 p-4 border-2 border-transparent",
              "border-default-100 data-[selected=true]:border-default-200",
              { "p-2": isCompact },
              classNames?.base,
            ),
            thumb: cn(
              "group-data-[hover=true]:border-primary",
              "group-data-[pressed=true]:w-7",
              "group-data-pressed:group-data-selected:ml-4",
              classNames?.thumb,
            ),
            label: classNames?.label,
          }}
          isSelected={props.value ?? props.checked ?? props.isSelected}
        >
          {children ? (
            children
          ) : (
            <div className="flex flex-col gap-1">
              <p
                className={cn("text-medium", {
                  "text-sm": isCompact,
                })}
              >
                {title}
              </p>
              {description != null && (
                <p className="text-tiny text-default-400">{description}</p>
              )}
            </div>
          )}
        </Switch>

        <LimeCollapse isOpen={!!error}>
          <p className="text-danger-500 p-1 text-xs">{error}</p>
        </LimeCollapse>
      </>
    );
  },
);

const IconSwitch = ({
  props,
  children,
}: {
  props: IconSwitchProps;
  children: React.ReactNode;
}) => {
  const {
    Component,
    slots,
    isSelected,
    getBaseProps,
    getInputProps,
    getWrapperProps,
  } = useSwitch({
    ...props,
    size: "md",
    isSelected: props.value ?? props.checked ?? props.isSelected,
  });

  const sizeValue = props.size ?? 56;
  const size = `${sizeValue}px`;
  const paddingVertical = children != null ? `${sizeValue / 2}px` : "8px";
  const paddingHorizontal = children != null ? "16px" : "8px";

  return (
    <div>
      <Component {...getBaseProps()}>
        <VisuallyHidden>
          <input {...getInputProps()} />
        </VisuallyHidden>
        <div
          {...getWrapperProps()}
          className={slots.wrapper({
            class: [
              "flex items-center justify-center",
              "bg-default/40 hover:bg-default-200 group-data-[selected=true]:hover:bg-primary-400 rounded-xl",
              {
                ...(children != null ? ["px-4"] : []),
              },
              props.classNames?.wrapper,
            ],
          })}
          style={{
            height: size,
            width: children != null ? "fit-content" : size,
            padding:
              children != null
                ? `${paddingVertical} ${paddingHorizontal}`
                : "8px",
          }}
        >
          {isSelected
            ? props.enabledIcon
            : (props.disabledIcon ?? props.enabledIcon)}

          {children != null && <div className="pl-2">{children}</div>}
        </div>
      </Component>

      <LimeCollapse isOpen={!!props.error}>
        <p className="text-danger-500 p-1 text-xs">{props.error}</p>
      </LimeCollapse>
    </div>
  );
};

LimeSwitch.displayName = "LimeSwitch";
